import * as React from "react";
import { Button, Dropdown, Modal, notification, Switch, Tooltip } from "antd";
import { ChevronDown, RefreshCw } from "react-feather";
import { linksOf, request, resourceLink } from "../../services/cpln";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../utils/time";
import { InstallLocation, InstallLocationData } from "../../pages/location/installModal";
import { UninstallLocation, UninstallLocationData } from "../../pages/location/uninstallModal";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { Location } from "../../schema/types/location/entity";
import { useGetLocationQuery } from "../../store/api/locationApi";
import { dateString } from "../../components/dateStringFn";
import { DateString } from "../../components/dateString";
import { ViewModal } from "../../components/modals/viewModal";
import { DeleteModal } from "../../components/modals/deleteModal";
import { TerraformImportModal } from "../../components/modals/tfImportModal";
import { K8sCrdExportModal } from "../../components/modals/k8sCrdExportModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { selectContext } from "../../store/contextSlice";
import { selectLabel } from "../../store/timezoneSlice";
import { Kind } from "../../schema/types/base";
import { applyActions } from "../../store/applySlice";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
  location: Location;
}
export const LocationDetailHeader: React.FC<Props> = ({ location }) => {
  const selfLink = linksOf(location).self!;

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { refetch, isFetching } = useGetLocationQuery(selfLink, { skip: !selfLink });

  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const { org, gvc } = useAppSelector(selectContext);
  const timezoneLabel = useAppSelector(selectLabel);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [action, setAction] = React.useState<
    | "edit" //
    | "view"
    | "clone"
    | "install-location"
    | "uninstall-location"
    | "delete"
    | "lock"
    | "tfImportCommand"
    | "k8sCrdExport"
    | ""
  >("");

  const kind: any = location.kind;

  const itemHelpers = itemHelpersBase({ org: org, gvc: gvc, kind: kind, name: location.name });

  const showDelete = location.provider === "byok";

  function getIsLocked(): boolean {
    return (location.tags || {})["cpln/protected"] == "true";
  }

  const onLock = async () => {
    try {
      const isLocked = getIsLocked();
      const link = resourceLink(kind as Kind, location.name);
      await request({ method: "patch", body: { tags: { "cpln/protected": !isLocked } }, url: link });
      notification.success({
        message: "Success",
        description: `Object was ${isLocked ? "unlocked" : "locked"} successfully`,
      });
      refetch();
      setAction("");
    } catch (e) {
      setAction("");
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  };

  const [installLocationData, setInstallLocationData] = React.useState<InstallLocationData | undefined>(undefined);
  const [uninstallLocationData, setUninstallLocationData] = React.useState<UninstallLocationData | undefined>(
    undefined,
  );
  const canExportToTerraform = terraformSupportedKinds.includes(kind as Kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind as Kind);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (terraformImportCommand) {
      setAction("tfImportCommand");
    }
  }, [terraformImportCommand]);

  async function handleActionsClick({ key }: any) {
    switch (key) {
      case "edit":
        editApply();
        break;
      case "install-location":
        const installNotificationKey: string = "install-location";
        notification.open({
          key: installNotificationKey,
          type: "info",
          message: "Install Location",
          description: "Fetching data from the server, please wait",
        });

        try {
          const url = location.links.find((location) => location.rel === "install")?.href;
          if (url) {
            const { data } = await request({
              url,
              service: "byok",
              method: "post",
            });
            setInstallLocationData(data as InstallLocationData);
          }
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          notification.warning({
            message: "Failed",
            description: errorMessage,
          });
        } finally {
          notification.destroy(installNotificationKey);
        }
        break;
      case "uninstall-location":
        const uninstallNotificationKey: string = "uninstall-location";
        notification.open({
          key: uninstallNotificationKey,
          type: "info",
          message: "Uninstall Location",
          description: "Fetching data from the server, please wait",
        });
        try {
          const url = location.links.find((location) => location.rel === "uninstall")?.href;
          if (url) {
            const { data } = await request({
              url,
              service: "byok",
              method: "post",
            });
            setUninstallLocationData(data as InstallLocationData);
          }
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          notification.warning({
            message: "Failed",
            description: errorMessage,
          });
        } finally {
          notification.destroy(uninstallNotificationKey);
        }
        break;
      case "view":
        setAction("view");
        break;
      case "export-json":
        await itemHelpers.handleExportJSON([selfLink], false);
        break;
      case "export-yaml":
        await itemHelpers.handleExportYaml([selfLink], false);
        break;
      case "export-json-slim":
        await itemHelpers.handleExportJSON([selfLink], true);
        break;
      case "export-yaml-slim":
        await itemHelpers.handleExportYaml([selfLink], true);
        break;
      case "export-terraform":
        setTerraformImportCommand(await itemHelpers.handleExportTerraform([selfLink]));
        break;
      case "export-k8s-crd":
        setAction("k8sCrdExport");
        break;
      case "clone":
        setAction("clone");
        break;
      case "delete":
        setAction("delete");
        break;
      default:
        break;
    }
  }

  async function editApply() {
    dispatch(applyActions.initialize({ itemLink: selfLink, previousPath: pathname }));
    navigate(`/console/apply`);
  }

  const items: ItemType<MenuItemType>[] = [];
  items.push({ key: "edit", label: "Edit & Apply" });
  if (location.provider === "byok") {
    items.push(
      { key: "install-location", label: `Install` },
      { key: "uninstall-location", label: `Uninstall` },
      { type: "divider" },
    );
  }
  items.push({ key: "view", label: "View" });
  const exportItem = {
    key: "ignore2",
    label: "Export",
    children: [
      { key: "export-json", label: "JSON" },
      { key: "export-yaml", label: "YAML" },
      { key: "export-json-slim", label: "JSON Slim" },
      { key: "export-yaml-slim", label: "YAML Slim" },
    ],
  };
  if (canExportToTerraform) {
    exportItem.children.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    exportItem.children.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  items.push(exportItem);
  if (showDelete) {
    items.push({ key: "delete", label: "Delete", danger: true });
  }

  function viewModalOnClose() {
    setAction("");
  }

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            {kind.toUpperCase()}
          </div>
          <div className="flex items-center">
            <div data-testid="detail-name" className="text-2xl font-semibold leading-none">
              {location.name}
            </div>
          </div>
        </div>
        <div className="flex-grow" />

        <div className="flex items-center gap-2">
          <div>{`Version ${location.version}`}</div>
          <Button
            disabled={isFetching}
            loading={isFetching}
            onClick={() => refetch()}
            icon={<RefreshCw size={17} />}
            size="small"
            type="default"
          />
          {items.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: items, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(location.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(location.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(location.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(location.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(location.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(location.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={location.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
            {location.provider === "byok" ? (
              <div>
                <Tooltip title={`Click to ${getIsLocked() ? "unlock" : "lock"} the object`} trigger={["hover"]}>
                  <Switch
                    value={getIsLocked()}
                    aria-label="Toggle Lock"
                    onChange={() => setAction("lock")}
                    checkedChildren={<LockOutlined />}
                    unCheckedChildren={<UnlockOutlined />}
                  />
                </Tooltip>
              </div>
            ) : null}
          </>
        </div>
      </div>
      {installLocationData ? (
        <InstallLocation installLocationData={installLocationData} onCancel={() => setInstallLocationData(undefined)} />
      ) : null}
      {uninstallLocationData ? (
        <UninstallLocation
          uninstallLocationData={uninstallLocationData}
          onCancel={() => setUninstallLocationData(undefined)}
        />
      ) : null}
      {action === "view" ? (
        <ViewModal
          kind={kind}
          link={selfLink}
          visible={true}
          onClose={viewModalOnClose}
          title={location.name}
          filename={itemHelpers.getFileName()}
        />
      ) : action === "delete" ? (
        <DeleteModal
          kind={kind}
          itemName={location.name}
          links={[selfLink]}
          onCancel={() => setAction("")}
          onComplete={() => setAction("")}
        />
      ) : action === "lock" ? (
        <Modal
          title={"Warning!"}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={onLock}
          cancelButtonProps={{ disabled: isFetching }}
          okButtonProps={{ disabled: isFetching, loading: isFetching }}
        >
          <div>
            {getIsLocked()
              ? "Unlocking the object will allow deletion. Are you sure?"
              : "Locking the object will prevent deletion. Are you sure?"}
          </div>
        </Modal>
      ) : action === "tfImportCommand" ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setAction("")} />
      ) : action === "k8sCrdExport" ? (
        <K8sCrdExportModal
          kind={kind}
          links={[selfLink]}
          onClose={() => {
            setAction("");
          }}
        />
      ) : null}
    </>
  );
};
